import React, { useState, useRef, useEffect } from "react";
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput, Animated, PanResponder, ActivityIndicator } from "react-native";
import { Ionicons, FontAwesome6 } from "@expo/vector-icons";
import { useApp } from "../context/AppContext";
import { PageTypes, getPageIcon } from "../pageTypes";
import { useTheme } from "../context/ThemeContext";
import { ComponentEditorProvider, useComponentEditor } from "../context/ComponentEditorContext";
import { PropertiesPanel } from "../components/PropertiesPanel";
import { useParams } from "react-router-dom";
import { useProject } from "../context/ProjectContext";
import { useDesign } from "../context/DesignContext";
import { useContent } from "../context/ContentContext";
import EditorModal, { MODAL_MODES } from "./modals/EditorModal";

// Left Sidebar Icons
const SidebarIcon = ({ name }) => (
	<View style={styles.sidebarIconContainer}>
		<Ionicons name={name} size={20} color="#666" />
	</View>
);

// Top Navigation Bar
const TopBar = ({ canUndo, canRedo, onUndo, onRedo, unsavedChanges, resetUnsavedChanges, savePages, onOpenModal }) => {
	const { pages, activePage } = useApp();
	const currentPage = pages.find((page) => page.id === activePage);
	const [showRevisionHistory, setShowRevisionHistory] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const handleUndo = () => {
		if (canUndo) {
			onUndo();
		}
	};

	const handleRedo = () => {
		if (canRedo) {
			onRedo();
		}
	};

	const handleSave = async () => {
		// manually call the savePages function
		setIsSaving(true);
		try {
			await savePages();
			resetUnsavedChanges();
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<View style={styles.topBar}>
			<View style={styles.topBarLeft}>
				<View style={styles.logoContainer}>
					<Text style={styles.logo}>☰</Text>
				</View>
				<TouchableOpacity style={styles.pageSelector}>
					<Text style={styles.pageText}>Page: {currentPage?.title || "Select a Page"}</Text>
					<Ionicons name="chevron-down" size={16} color="#fff" />
				</TouchableOpacity>
			</View>
			<View style={styles.topBarCenter}>
				<View style={styles.devicePreview}>
					<TouchableOpacity style={styles.deviceButton}>
						<Ionicons name="desktop-outline" size={18} color="#fff" />
					</TouchableOpacity>
					<TouchableOpacity style={styles.deviceButton}>
						<Ionicons name="phone-portrait-outline" size={18} color="#666" />
					</TouchableOpacity>
					<TouchableOpacity style={styles.deviceButton}>
						<Ionicons name="phone-landscape-outline" size={18} color="#666" />
					</TouchableOpacity>
				</View>
				<Text style={styles.dimensions}>1440 (PX)</Text>
				<Text style={styles.separator}>/</Text>
				<Text style={styles.dimensions}>80 (%)</Text>
				<TouchableOpacity style={styles.settingsButton}>
					<Ionicons name="settings-outline" size={18} color="#666" />
				</TouchableOpacity>
			</View>
			<View style={styles.topBarRight}>
				<TouchableOpacity style={styles.designButton} onPress={() => onOpenModal(MODAL_MODES.ICON_PICKER)}>
					<Ionicons name="apps-outline" size={18} color="#fff" />
					<Text style={styles.designButtonText}>Icons</Text>
				</TouchableOpacity>
				<TouchableOpacity style={styles.designButton} onPress={() => onOpenModal(MODAL_MODES.COLOR_SWATCH)}>
					<Ionicons name="color-palette-outline" size={18} color="#fff" />
					<Text style={styles.designButtonText}>Colors</Text>
				</TouchableOpacity>
				<TouchableOpacity style={styles.designButton} onPress={() => onOpenModal(MODAL_MODES.STYLER)}>
					<Ionicons name="text-outline" size={18} color="#fff" />
					<Text style={styles.designButtonText}>Styler</Text>
				</TouchableOpacity>
				<TouchableOpacity style={styles.designButton} onPress={() => onOpenModal(MODAL_MODES.COMPONENT_CREATOR)}>
					<Ionicons name="construct-outline" size={18} color="#fff" />
					<Text style={styles.designButtonText}>Creator</Text>
				</TouchableOpacity>
				<View style={styles.divider} />
				<TouchableOpacity style={[styles.iconButton, !canUndo && styles.iconButtonDisabled]} onPress={handleUndo} disabled={!canUndo}>
					<Ionicons name="arrow-undo-outline" size={18} color={canUndo ? "#fff" : "#666"} />
				</TouchableOpacity>
				<TouchableOpacity style={[styles.iconButton, !canRedo && styles.iconButtonDisabled]} onPress={handleRedo} disabled={!canRedo}>
					<Ionicons name="arrow-redo-outline" size={18} color={canRedo ? "#fff" : "#666"} />
				</TouchableOpacity>
				{unsavedChanges > 0 ? (
					<>
						<View style={styles.unsavedChangesGem}>
							<Ionicons name="warning-outline" size={14} color="goldenrod" />
							<Text style={styles.unsavedChangesText}>
								{unsavedChanges} unsaved {unsavedChanges === 1 ? "change" : "changes"}
							</Text>
						</View>
						<TouchableOpacity style={styles.publishButton} onPress={handleSave} disabled={isSaving}>
							{isSaving ? (
								<ActivityIndicator size="small" color="#2a2a2a" />
							) : (
								<>
									<Ionicons name="arrow-up" size={14} color="#000" />
									<Text style={styles.publishText}>Save</Text>
								</>
							)}
						</TouchableOpacity>
					</>
				) : null}
				<TouchableOpacity style={[styles.iconButton, showRevisionHistory && styles.iconButtonActive]} onPress={() => setShowRevisionHistory(!showRevisionHistory)}>
					<Ionicons name="git-branch-outline" size={18} color={showRevisionHistory ? "#82C91E" : "#fff"} />
				</TouchableOpacity>
			</View>
			{showRevisionHistory && <RevisionHistory />}
		</View>
	);
};

// Revision History Panel
const RevisionHistory = () => {
	const { pages, activePage, revertToRevision } = useApp();
	const currentPage = pages.find((page) => page.id === activePage);

	if (!currentPage || !currentPage.revisions) {
		return (
			<View style={styles.revisionHistoryEmpty}>
				<Text style={styles.emptyText}>No revision history available</Text>
			</View>
		);
	}

	return (
		<View style={styles.revisionHistory}>
			<View style={styles.revisionHeader}>
				<Text style={styles.revisionTitle}>Revision History</Text>
				<Text style={styles.revisionSubtitle}>Click a revision to preview or restore it</Text>
			</View>
			<ScrollView style={styles.revisionList}>
				{currentPage.revisions.map((revision) => (
					<TouchableOpacity key={revision.id} style={styles.revisionItem} onPress={() => revertToRevision(currentPage.id, revision.id)}>
						<View style={styles.revisionInfo}>
							<Text style={styles.revisionType}>{revision.revisionType.charAt(0).toUpperCase() + revision.revisionType.slice(1)}</Text>
							<Text style={styles.revisionTime}>{new Date(revision.createdAt).toLocaleString()}</Text>
						</View>
						<Ionicons name="git-commit-outline" size={16} color="#666" />
					</TouchableOpacity>
				))}
			</ScrollView>
		</View>
	);
};

const Badge = ({ text, type = "default" }) => {
	const badgeStyles = {
		default: { backgroundColor: "#82C91E" },
		new: { backgroundColor: "#4C6EF5" },
		warning: { backgroundColor: "#FF922B" },
		required: { backgroundColor: "#FF6B6B" },
	};

	return (
		<View style={[styles.badge, badgeStyles[type]]}>
			<Text style={styles.badgeText}>{text}</Text>
		</View>
	);
};

// Component Preview Box with drag functionality
const ComponentBox = ({ title, onAdd, icon, description, badges = [] }) => {
	const [isHovered, setIsHovered] = useState(false);
	const borderAnim = useRef(new Animated.Value(0)).current;

	useEffect(() => {
		if (isHovered) {
			Animated.timing(borderAnim, {
				toValue: 0.5,
				duration: 200,
				useNativeDriver: false,
			}).start();
		} else {
			Animated.timing(borderAnim, {
				toValue: 0,
				duration: 200,
				useNativeDriver: false,
			}).start();
		}
	}, [isHovered]);

	// Map titles to appropriate icons if no icon is provided
	const getIconName = (title) => {
		if (icon) return icon;
		switch (title.toLowerCase()) {
			case "profile":
				return "person-outline";
			case "feed":
				return "list-outline";
			case "events":
				return "calendar-outline";
			case "community":
				return "people-outline";
			case "search":
				return "search-outline";
			default:
				return "document-outline";
		}
	};

	// Map titles to descriptions if no description is provided
	const getDescription = (title) => {
		if (description) return description;
		switch (title.toLowerCase()) {
			case "profile":
				return "User profile and settings page";
			case "products":
				return "Product catalog and listings";
			case "feed":
				return "Main content feed and updates";
			case "events":
				return "Calendar and event listings";
			case "community":
				return "Community hub and interactions";
			case "search":
				return "Search and discovery page";
			default:
				return "New page template";
		}
	};

	const borderColor = borderAnim.interpolate({
		inputRange: [0, 1],
		outputRange: ["transparent", "#0E98FF"],
	});

	return (
		<Animated.View
			style={[
				styles.componentBoxWrapper,
				{
					borderColor: borderColor,
					shadowColor: "#0E98FF",
					shadowOffset: { width: 0, height: 0 },
					shadowRadius: 8,
					shadowOpacity: borderAnim,
				},
			]}>
			<View style={styles.badgeContainer}>
				{badges.map((badge, index) => (
					<Badge key={index} text={badge.text} type={badge.type} />
				))}
			</View>
			<TouchableOpacity style={styles.componentBox} onPress={() => onAdd && onAdd(title)} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
				<View style={styles.boxContent}>
					<View style={styles.boxHeader}>
						<Ionicons name={getIconName(title)} size={18} color="#666" />
						<Text style={styles.componentText}>{title}</Text>
					</View>
					<Text style={styles.componentDescription}>{getDescription(title)}</Text>
				</View>
			</TouchableOpacity>
		</Animated.View>
	);
};

// Menu Bar Component for Phone View
const MenuBar = () => {
	const { pages, activePage, setActivePage, updatePages } = useApp();
	const { content } = useContent();
	const theme = useTheme();
	const [hoveredItem, setHoveredItem] = useState(null);
	const [localPages, setLocalPages] = useState([]);
	const animatedValues = useRef({});
	const hasReordered = useRef(false);

	useEffect(() => {
		// Initialize local pages from context
		setLocalPages(pages);

		// Initialize animated values for each page
		pages.forEach((page) => {
			if (!animatedValues.current[page.id]) {
				animatedValues.current[page.id] = new Animated.Value(0);
			}
		});

		// Reset reordering flag when pages change from outside
		hasReordered.current = false;
	}, [pages]);

	if (localPages.length === 0) {
		return null;
	}

	const handleMoveLeft = (index, pageId) => {
		if (index === 0) return; // Can't move left if first item

		// Get the target pages to swap
		const leftPage = localPages[index - 1];

		// Animate current page to the left
		Animated.timing(animatedValues.current[pageId], {
			toValue: -1,
			duration: 200,
			useNativeDriver: true,
		}).start();

		// Animate left page to the right
		Animated.timing(animatedValues.current[leftPage.id], {
			toValue: 1,
			duration: 200,
			useNativeDriver: true,
		}).start(() => {
			// After animation completes, reset animated values
			animatedValues.current[pageId].setValue(0);
			animatedValues.current[leftPage.id].setValue(0);

			// Update local state only
			const newPages = [...localPages];
			[newPages[index - 1], newPages[index]] = [newPages[index], newPages[index - 1]];
			setLocalPages(newPages);

			// Set flag that we've manually reordered
			hasReordered.current = true;

			// Schedule the update to global state
			setTimeout(() => {
				if (hasReordered.current) {
					updatePages(newPages.map((p) => p.id));
					hasReordered.current = false;
				}
			}, 200);
		});
	};

	const handleMoveRight = (index, pageId) => {
		if (index === localPages.length - 1) return; // Can't move right if last item

		// Get the target pages to swap
		const rightPage = localPages[index + 1];

		// Animate current page to the right
		Animated.timing(animatedValues.current[pageId], {
			toValue: 1,
			duration: 200,
			useNativeDriver: true,
		}).start();

		// Animate right page to the left
		Animated.timing(animatedValues.current[rightPage.id], {
			toValue: -1,
			duration: 200,
			useNativeDriver: true,
		}).start(() => {
			// After animation completes, reset animated values
			animatedValues.current[pageId].setValue(0);
			animatedValues.current[rightPage.id].setValue(0);

			// Update local state only
			const newPages = [...localPages];
			[newPages[index], newPages[index + 1]] = [newPages[index + 1], newPages[index]];
			setLocalPages(newPages);

			// Set flag that we've manually reordered
			hasReordered.current = true;

			// Schedule the update to global state
			setTimeout(() => {
				if (hasReordered.current) {
					updatePages(newPages.map((p) => p.id));
					hasReordered.current = false;
				}
			}, 200);
		});
	};

	return (
		<View style={[styles.menuBar, { backgroundColor: theme.colors.systemBackground.primary, borderTopColor: theme.colors.separator }]}>
			{localPages.map((page, index) => (
				<Animated.View
					key={page.id + "page-menu-item"}
					style={{
						transform: [
							{
								translateX: animatedValues.current[page.id]
									? animatedValues.current[page.id].interpolate({
											inputRange: [-1, 0, 1],
											outputRange: [-80, 0, 80],
									  })
									: 0,
							},
						],
					}}>
					<TouchableOpacity style={[styles.menuItem]} onPress={() => setActivePage(page.id)} onMouseEnter={() => setHoveredItem(page.id)} onMouseLeave={() => setHoveredItem(null)}>
						<FontAwesome6 name={content[`${page.type}Page`]?.pageIcon || page.icon} size={24} color={activePage === page.id ? theme.colors.primary : theme.colors.textSecondary} />
						<Text style={[styles.menuText, { color: activePage === page.id ? theme.colors.primary : theme.colors.textSecondary }]}>{content[`${page.type}Page`]?.pageTitle || page.title}</Text>

						{hoveredItem === page.id && (
							<>
								{index > 0 && (
									<TouchableOpacity
										style={styles.caretLeft}
										onPress={(e) => {
											e.stopPropagation();
											handleMoveLeft(index, page.id);
										}}>
										<Ionicons name="chevron-back" size={14} color="#66666655" />
									</TouchableOpacity>
								)}

								{index < localPages.length - 1 && (
									<TouchableOpacity
										style={styles.caretRight}
										onPress={(e) => {
											e.stopPropagation();
											handleMoveRight(index, page.id);
										}}>
										<Ionicons name="chevron-forward" size={14} color="#66666655" />
									</TouchableOpacity>
								)}
							</>
						)}
					</TouchableOpacity>
				</Animated.View>
			))}
		</View>
	);
};

const PageItem = ({ page, index, totalPages }) => {
	const { activePage, setActivePage, deletePage, updatePages, pages } = useApp();
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [position] = useState(new Animated.Value(0));

	const handleDelete = (e) => {
		e.stopPropagation();
		console.log("Delete clicked, showing confirmation");
		setShowDeleteConfirm(true);
	};

	const handleConfirmDelete = (e) => {
		e.stopPropagation();
		deletePage(page.id);
		setShowDeleteConfirm(false);
	};

	const handleCancelDelete = (e) => {
		e.stopPropagation();
		setShowDeleteConfirm(false);
	};

	const handleMoveUp = (e) => {
		console.log(pages);
		e.stopPropagation();
		if (index === 0) return; // Already at top

		// Animate the position up
		Animated.timing(position, {
			toValue: -40,
			duration: 200,
			useNativeDriver: true,
		}).start(() => {
			const newPages = [...pages];
			[newPages[index - 1], newPages[index]] = [newPages[index], newPages[index - 1]];
			updatePages(newPages.map((p) => p.id));
			position.setValue(0);
		});
	};

	const handleMoveDown = (e) => {
		e.stopPropagation();
		if (index === totalPages - 1) return; // Already at bottom

		// Animate the position down
		Animated.timing(position, {
			toValue: 40,
			duration: 500,
			useNativeDriver: true,
		}).start(() => {
			const newPages = [...pages];
			[newPages[index], newPages[index + 1]] = [newPages[index + 1], newPages[index]];
			updatePages(newPages.map((p) => p.id));
			position.setValue(0);
		});
	};

	return (
		<Animated.View style={[styles.pageItem, { transform: [{ translateY: position }] }]}>
			<TouchableOpacity style={[styles.pageItemContent, activePage === page.id && styles.activePageItem]} onPress={() => setActivePage(page.id)}>
				<View style={styles.pageItemLeft}>
					<Text style={styles.pageItemText}>{page.title}</Text>
				</View>
				<View style={styles.pageArrowsContainer}>
					{index > 0 && (
						<TouchableOpacity style={styles.pageArrow} onPress={handleMoveUp}>
							<Ionicons name="chevron-up" size={14} color="#666" />
						</TouchableOpacity>
					)}
					{index < totalPages - 1 && (
						<TouchableOpacity style={styles.pageArrow} onPress={handleMoveDown}>
							<Ionicons name="chevron-down" size={14} color="#666" />
						</TouchableOpacity>
					)}
				</View>
				<View style={styles.pageItemRight}>
					<TouchableOpacity onPress={handleDelete} style={styles.deleteButton}>
						<Ionicons name="trash-outline" size={20} color="#ff6b6b" />
					</TouchableOpacity>
				</View>
			</TouchableOpacity>

			{showDeleteConfirm && (
				<View style={[styles.deleteConfirm]}>
					<Text style={styles.deleteConfirmText}>Delete this page?</Text>
					<View style={styles.deleteConfirmButtons}>
						<TouchableOpacity style={styles.deleteConfirmCancel} onPress={handleCancelDelete}>
							<Text style={styles.deleteConfirmCancelText}>Cancel</Text>
						</TouchableOpacity>
						<TouchableOpacity style={styles.deleteConfirmDelete} onPress={handleConfirmDelete}>
							<Text style={styles.deleteConfirmDeleteText}>Delete</Text>
						</TouchableOpacity>
					</View>
				</View>
			)}
		</Animated.View>
	);
};

// PagesList Component
const PagesList = () => {
	const { pages } = useApp();

	return (
		<View style={styles.pagesListContainer}>
			<View style={styles.pagesListHeader}>
				<Text style={styles.pagesListTitle}>Pages</Text>
				<Text style={styles.pagesListSubtitle}>Use arrows to reorder pages</Text>
			</View>
			<View style={styles.pagesList}>
				{pages.map((page, index) => (
					<PageItem key={page.id} page={page} index={index} totalPages={pages.length} />
				))}
			</View>
		</View>
	);
};

// Left Sidebar
const LeftSidebar = ({ mode }) => {
	const { pages, addPage, activePage, updatePageSections, updatePage } = useApp();
	const { activeTab, onPageChange } = useComponentEditor();
	const [activeSection, setActiveSection] = useState("Pages");
	const [showAddModal, setShowAddModal] = useState(false);
	const [selectedPage, setSelectedPage] = useState(null);
	const [expandedSections, setExpandedSections] = useState({});
	const { content, updateContent, fetchContent } = useContent();
	const currentPage = pages.find((page) => page.id === activePage);
	const currentPageType = currentPage?.type;
	const pageTypeConfig = currentPageType ? PageTypes[currentPageType] : null;
	const [updateType, setUpdateType] = useState(null);
	const handleAddPage = (pageTitle) => {
		setSelectedPage(pageTitle);
		setShowAddModal(true);
	};

	const handleConfirmAdd = () => {
		const newPage = addPage(selectedPage.toLowerCase());
		setShowAddModal(false);
		setSelectedPage(null);
	};
	useEffect(() => {
		if (!updateType) return;
		setUpdateType(null);
		const stateSnapshot = {
			design: {}, //we don't need to fetch design for this
			content: fetchContent(updateType),
			component: {},
		};
		console.log("stateSnapshot", stateSnapshot);
		// Access onPageChange directly from the ComponentEditorContext
		// This will update the unsavedChanges count
		if (onPageChange) {
			onPageChange({
				contextStates: stateSnapshot,
			});
		}
	}, [updateType]);
	const handleAddComponent = async (componentType) => {
		if (!pageTypeConfig || !currentPage) return;

		const sectionType = pageTypeConfig.sectionTypes[componentType];
		if (!sectionType) return;

		const newComponent = sectionType.getDefault();
		//we need to either get the current focused canvas!

		switch (currentPageType) {
			case "profile":
				updateContent("profile", {
					[activeTab]: newComponent,
				});

				break;
			default:
				console.error("No page type handled for this page");
				break;
		}
		setUpdateType(currentPageType);
	};

	const toggleSectionExpanded = (sectionId) => {
		setExpandedSections((prev) => ({
			...prev,
			[sectionId]: !prev[sectionId],
		}));
	};

	const getAvailableComponents = () => {
		if (!pageTypeConfig) return [];

		return Object.entries(pageTypeConfig.sectionTypes)
			.filter(([key]) => key !== "header" && key !== "footer" && key !== "content") // Filter out base components
			.map(([key, section]) => ({
				type: key,
				title: section.getDefault().name,
				description: `Add a ${key} section to your page`,
				icon: getComponentIcon(key),
			}));
	};

	const getComponentIcon = (type) => {
		const iconMap = {
			featured: "star-outline",
			events: "calendar-outline",
			activity: "pulse-outline",
			quickAccess: "grid-outline",
			announcements: "megaphone-outline",
			// Add more mappings as needed
		};
		return iconMap[type] || "cube-outline";
	};

	const renderQuickAdd = () => (
		<>
			<View style={styles.quickAdd}>
				<View style={styles.sectionHeader}>
					<Text style={styles.sectionTitle}>Quick Add</Text>
					<View style={styles.sectionHeaderIcons}>
						<Ionicons name="information-circle-outline" size={16} color="#666" />
						<Ionicons name="close" size={16} color="#666" />
					</View>
				</View>
				<View style={styles.tabGroup}>
					<TouchableOpacity style={[styles.tab, activeSection === "Pages" && styles.activeTab]} onPress={() => setActiveSection("Pages")}>
						<Text style={styles.tabText}>Pages</Text>
					</TouchableOpacity>
					<TouchableOpacity style={[styles.tab, activeSection === "Components" && styles.activeTab]} onPress={() => setActiveSection("Components")}>
						<Text style={styles.tabText}>Components</Text>
					</TouchableOpacity>
				</View>
			</View>

			{activeSection === "Pages" && (
				<View style={styles.componentsSection}>
					<View style={styles.sectionHeader}>
						<SidebarIcon name="apps-outline" />
						<Text style={styles.sectionTitle}>App Pages</Text>
						<Ionicons name="chevron-down" size={16} color="#666" />
					</View>
					<View style={styles.componentGrid}>
						<ComponentBox title="Profile" onAdd={handleAddPage} badges={[{ text: "New!", type: "new" }]} />
						<ComponentBox title="Products" onAdd={handleAddPage} badges={[{ text: "New!", type: "new" }]} />
						<ComponentBox title="Feed" onAdd={handleAddPage} badges={[{ text: "Popular", type: "default" }]} />
						<ComponentBox title="Events" onAdd={handleAddPage} badges={[{ text: "Requires Calendar", type: "required" }]} />
						<ComponentBox title="Community" onAdd={handleAddPage} badges={[{ text: "Requires Accounts", type: "warning" }]} />
						<ComponentBox title="Search" onAdd={handleAddPage} />
					</View>
				</View>
			)}

			{activeSection === "Components" && currentPageType && (
				<View style={styles.componentsSection}>
					<View style={styles.sectionHeader}>
						<SidebarIcon name="cube-outline" />
						<Text style={styles.sectionTitle}>{currentPageType.charAt(0).toUpperCase() + currentPageType.slice(1)} Components</Text>
						<Ionicons name="chevron-down" size={16} color="#666" />
					</View>
					<View style={styles.componentGrid}>
						{getAvailableComponents().map((component) => (
							<ComponentBox key={component.type} title={component.title} description={component.description} icon={component.icon} onAdd={() => handleAddComponent(component.type)} />
						))}
						{getAvailableComponents().length === 0 && <Text style={styles.emptyStateText}>No components available for this page type</Text>}
					</View>
				</View>
			)}
		</>
	);

	const renderLayers = () => {
		if (!currentPage) {
			return (
				<View style={styles.layersEmptyState}>
					<Text style={styles.layersEmptyText}>Select a page to view its layers</Text>
				</View>
			);
		}

		return (
			<View style={styles.layersContainer}>
				<View style={styles.layersHeader}>
					<Text style={styles.layersTitle}>Layers</Text>
					<Text style={styles.layersSubtitle}>Drag to reorder layers</Text>
				</View>
				<View style={styles.layersList}>
					{currentPage.content.sections.map((section) => (
						<View key={section.id} style={styles.layerSection}>
							<TouchableOpacity style={styles.layerSectionHeader} onPress={() => toggleSectionExpanded(section.id)}>
								<View style={styles.layerSectionLeft}>
									<Ionicons name={expandedSections[section.id] ? "chevron-down" : "chevron-forward"} size={16} color="#666" />
									<Text style={styles.layerSectionTitle}>{section.name}</Text>
								</View>
								<TouchableOpacity>
									<Ionicons name="settings-outline" size={16} color="#666" />
								</TouchableOpacity>
							</TouchableOpacity>
							{expandedSections[section.id] && (
								<View style={styles.layerItems}>
									{section.layers.map((layer) => (
										<View
											key={layer.id}
											style={[styles.layerItem, draggedLayer?.layer.id === layer.id && styles.layerItemDragging]}
											onDragStart={() => handleLayerDragStart(section.id, layer)}
											onDragOver={(e) => handleLayerDragOver(e, section.id, layer)}
											onDragEnd={handleLayerDragEnd}
											draggable>
											<View style={styles.layerItemLeft}>
												<Ionicons name="menu" size={16} color="#666" style={styles.dragHandle} />
												<Ionicons name="square-outline" size={16} color="#666" />
												<Text style={styles.layerItemTitle}>{layer.name}</Text>
											</View>
											<View style={styles.layerItemRight}>
												<TouchableOpacity>
													<Ionicons name="eye-outline" size={16} color="#666" />
												</TouchableOpacity>
												<TouchableOpacity>
													<Ionicons name="lock-open-outline" size={16} color="#666" />
												</TouchableOpacity>
											</View>
										</View>
									))}
								</View>
							)}
						</View>
					))}
				</View>
			</View>
		);
	};

	return (
		<ScrollView style={styles.leftSidebar} showsVerticalScrollIndicator={false}>
			{mode === "quickAdd" && renderQuickAdd()}
			{mode === "pages" && <PagesList />}
			{mode === "layers" && renderLayers()}

			{showAddModal && (
				<View style={styles.modalOverlay}>
					<View style={styles.modal}>
						<View style={styles.modalHeader}>
							<Text style={styles.modalTitle}>Add {selectedPage} Page</Text>
							<TouchableOpacity onPress={() => setShowAddModal(false)}>
								<Ionicons name="close" size={24} color="#666" />
							</TouchableOpacity>
						</View>
						<Text style={styles.modalText}>This will add a new {selectedPage.toLowerCase()} page to your app with default layout and components.</Text>
						<View style={styles.modalActions}>
							<TouchableOpacity style={styles.modalCancelButton} onPress={() => setShowAddModal(false)}>
								<Text style={styles.modalCancelText}>Cancel</Text>
							</TouchableOpacity>
							<TouchableOpacity style={styles.modalAddButton} onPress={handleConfirmAdd}>
								<Text style={styles.modalAddText}>Add Page</Text>
							</TouchableOpacity>
						</View>
					</View>
				</View>
			)}
		</ScrollView>
	);
};

// Canvas Component
const Canvas = () => {
	const { pages, activePage, isLoading, error } = useApp();
	const currentPage = pages.find((page) => page.id === activePage);
	const PageComponent = currentPage ? PageTypes[currentPage.type]?.component : null;

	if (isLoading) {
		return (
			<View style={[styles.canvas, styles.centerContent]}>
				<Text style={styles.loadingText}>Loading your app...</Text>
			</View>
		);
	}

	if (error) {
		return (
			<View style={[styles.canvas, styles.centerContent]}>
				<Text style={styles.errorText}>Error: {error}</Text>
				<TouchableOpacity style={styles.retryButton}>
					<Text style={styles.retryButtonText}>Retry</Text>
				</TouchableOpacity>
			</View>
		);
	}

	return (
		<View style={styles.canvas}>
			<View style={styles.canvasHeader}>
				<Text style={styles.canvasTitle}>iPhone 15 Pro</Text>
				<Text style={styles.canvasUrl}>Demo Application (0.0.1)</Text>
			</View>
			<View style={styles.canvasContent}>
				<View style={styles.phoneContainer}>
					<View style={styles.phoneFrame}>
						<View style={styles.phoneScreen}>
							<StatusBar />
							{currentPage ? (
								<View style={styles.phoneContent}>
									{PageComponent ? (
										<PageComponent content={currentPage.content} />
									) : (
										<>
											<Text style={styles.pageTitle}>{currentPage.title}</Text>
											<Text style={styles.placeholderText}>This is the {currentPage.type} page content</Text>
										</>
									)}
								</View>
							) : (
								<View style={[styles.phoneContent, styles.emptyStateContent]}>
									<Text style={styles.placeholderText}>Add a page from the Quick Add menu to get started</Text>
								</View>
							)}
							<MenuBar />
						</View>
					</View>
				</View>
			</View>
		</View>
	);
};

// Main Editor Component
const Editor = () => {
	const [sidebarMode, setSidebarMode] = useState("quickAdd");
	const { appId } = useParams();
	const { loadProject, isLoading, error, currentProject } = useProject();
	const { pages, setPages } = useApp();
	const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
	const [pageChanges, setPageChanges] = useState([]);
	const [currentChangeIndex, setCurrentChangeIndex] = useState(-1);
	const [unsavedChanges, setUnsavedChanges] = useState(0);
	const { designSnapshot, designRestore } = useDesign();
	const { contentSnapshot, contentRestore } = useContent();
	const { saveApp } = useApp();

	// State for the modal
	const [showEditorModal, setShowEditorModal] = useState(false);
	const [editorModalMode, setEditorModalMode] = useState(MODAL_MODES.ICON_PICKER);
	const [modalCallback, setModalCallback] = useState(null); // State for the modal callback

	// Computed state for undo/redo availability
	const canUndo = currentChangeIndex < pageChanges.length - 1;
	const canRedo = currentChangeIndex > 0;

	// Function to reset unsaved changes count
	const resetUnsavedChanges = () => {
		setUnsavedChanges(0);
	};

	// Function to open the modal, now accepting an onComplete callback
	const handleOpenModal = (mode, onCompleteCallback) => {
		setEditorModalMode(mode);
		// Wrap the callback setting in a function to ensure it's stored correctly
		setModalCallback(() => (data) => {
			if (onCompleteCallback) {
				onCompleteCallback(data);
			}
		});
		setShowEditorModal(true);
	};

	// Initialize history with current page state when pages load
	useEffect(() => {
		if (pages.length > 0 && pageChanges.length === 0) {
			// Create a single initial snapshot containing all pages
			const initialState = {
				pagesSnapshot: [...pages],
				timestamp: new Date().toISOString(),
				changeId: `initial_state`,
				changedPageId: null,
			};

			setPageChanges([initialState]);
			setCurrentChangeIndex(0);
			console.log("Initialized history with initial pages state");
		}
	}, [pages, pageChanges.length]);

	// Update unsavedChanges when pageChanges changes
	useEffect(() => {
		// Subtract 1 from the length to exclude the initial state
		setUnsavedChanges(Math.max(0, pageChanges.length - 1));
	}, [pageChanges]);

	useEffect(() => {
		if (appId && !hasAttemptedLoad && !isLoading) {
			setHasAttemptedLoad(true);
			loadProject(appId).catch(() => {
				console.log("Failed to load project");
			});
		}
	}, [appId, loadProject, hasAttemptedLoad, isLoading]);
	useEffect(() => {
		if (currentProject) {
			console.log("Current project", currentProject);
			designRestore(currentProject.contexts.design);
			contentRestore(currentProject.contexts.content);
		}
	}, [currentProject]);
	// Function to handle adding a page change
	const handlePageChange = () => {
		// we don't need to pass newData here because we're using the context to update the state
		setPageChanges((prevChanges) => {
			// If we're not at the beginning of the timeline, remove future changes
			const newChanges = prevChanges.slice(currentChangeIndex);
			const newData = {
				design: designSnapshot(),
				content: contentSnapshot(),
			};
			// Prepend the new change with full app state
			const updatedChanges = [
				{
					snapshot: newData,
					timestamp: new Date().toISOString(),
					changeId: `change_${Date.now()}`,
				},
				...newChanges,
			];

			return updatedChanges;
		});
	};
	const handleSave = () => {
		//collect the LATEST state snapshot from pageChanges
		console.log("pageChanges", pageChanges[0]);
		const latestState = pageChanges[0];
		saveApp(latestState);
	};
	// Function to handle undo
	const handleUndo = () => {
		if (currentChangeIndex < pageChanges.length - 1) {
			const nextState = pageChanges[currentChangeIndex + 1];

			if (nextState && nextState.snapshot) {
				console.log("Applying undo to entire app state");
				designRestore(nextState.snapshot.design);
				contentRestore(nextState.snapshot.content);
				setCurrentChangeIndex(currentChangeIndex + 1);
			}
		}
	};

	// Function to handle redo
	const handleRedo = () => {
		if (currentChangeIndex > 0) {
			const nextState = pageChanges[currentChangeIndex - 1];
			if (nextState && nextState.snapshot) {
				console.log("Applying undo to entire app state");
				designRestore(nextState.snapshot.design);
				contentRestore(nextState.snapshot.content);
				setCurrentChangeIndex(currentChangeIndex - 1);
			}
		}
	};

	if (isLoading) {
		return (
			<View style={[styles.container, styles.centerContent]}>
				<Text style={styles.loadingText}>Loading project...</Text>
			</View>
		);
	}

	if (error) {
		return (
			<View style={[styles.container, styles.centerContent]}>
				<Text style={styles.errorText}>Error loading project: {error}</Text>
				<TouchableOpacity
					style={styles.retryButton}
					onPress={() => {
						setHasAttemptedLoad(false);
					}}>
					<Text style={styles.retryButtonText}>Retry</Text>
				</TouchableOpacity>
			</View>
		);
	}

	return (
		<ComponentEditorProvider onPageChange={handlePageChange}>
			<View style={styles.container}>
				<TopBar canUndo={canUndo} canRedo={canRedo} onUndo={handleUndo} onRedo={handleRedo} unsavedChanges={unsavedChanges} resetUnsavedChanges={resetUnsavedChanges} savePages={handleSave} onOpenModal={handleOpenModal} />
				<View style={styles.editorContent}>
					<View key="iconBar" style={styles.iconBar}>
						<TouchableOpacity style={[styles.iconBarButton, sidebarMode === "quickAdd" && styles.activeIconBarButton]} onPress={() => setSidebarMode("quickAdd")}>
							<Ionicons name="add" size={20} color={sidebarMode === "quickAdd" ? "#82C91E" : "#fff"} />
						</TouchableOpacity>
						<TouchableOpacity style={[styles.iconBarButton, sidebarMode === "pages" && styles.activeIconBarButton]} onPress={() => setSidebarMode("pages")}>
							<Ionicons name="apps-outline" size={20} color={sidebarMode === "pages" ? "#82C91E" : "#666"} />
						</TouchableOpacity>
						<TouchableOpacity style={[styles.iconBarButton, sidebarMode === "layers" && styles.activeIconBarButton]} onPress={() => setSidebarMode("layers")}>
							<Ionicons name="layers-outline" size={20} color={sidebarMode === "layers" ? "#82C91E" : "#666"} />
						</TouchableOpacity>
						<TouchableOpacity style={styles.iconBarButton}>
							<Ionicons name="folder-outline" size={20} color="#666" />
						</TouchableOpacity>
						<TouchableOpacity style={styles.iconBarButton}>
							<Ionicons name="grid-outline" size={20} color="#666" />
						</TouchableOpacity>
						<TouchableOpacity style={styles.iconBarButton}>
							<Ionicons name="color-palette-outline" size={20} color="#666" />
						</TouchableOpacity>
					</View>
					<LeftSidebar mode={sidebarMode} />
					<Canvas key={`canvas-${currentChangeIndex}`} />
					<PropertiesPanel onOpenModal={handleOpenModal} />
				</View>

				{/* Editor Modal at the root level */}
				<EditorModal
					visible={showEditorModal}
					onClose={() => {
						setShowEditorModal(false);
						setModalCallback(null); // Clear callback on close
					}}
					initialMode={editorModalMode}
					onComplete={modalCallback} // Pass the callback state to the modal
				/>
			</View>
		</ComponentEditorProvider>
	);
};

const StatusBar = () => {
	const time = new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "numeric", hour12: true });

	return (
		<View style={styles.statusBar}>
			<View style={styles.statusBarLeft}>
				<Text style={styles.statusBarTime}>{time}</Text>
			</View>
			<View style={styles.statusBarRight}>
				<View style={styles.statusBarSignal}>
					<Ionicons name="cellular" size={20} color="#000" />
				</View>
				<View style={styles.statusBarBattery}>
					<Ionicons name="battery-half" size={26} />
				</View>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: "#1A1A1A",
	},
	editorContent: {
		flex: 1,
		flexDirection: "row",
	},
	topBar: {
		height: 48,
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		backgroundColor: "#1A1A1A",
		borderBottomWidth: 1,
		borderBottomColor: "#333",
		paddingHorizontal: 16,
	},
	topBarLeft: {
		flexDirection: "row",
		alignItems: "center",
	},
	logoContainer: {
		width: 32,
		height: 32,
		justifyContent: "center",
		alignItems: "center",
	},
	logo: {
		color: "#82C91E",
		fontSize: 20,
	},
	pageSelector: {
		flexDirection: "row",
		alignItems: "center",
		backgroundColor: "#2A2A2A",
		padding: 6,
		borderRadius: 4,
		gap: 8,
	},
	pageText: {
		color: "#fff",
		fontSize: 13,
	},
	topBarCenter: {
		flexDirection: "row",
		alignItems: "center",
		gap: 12,
	},
	devicePreview: {
		flexDirection: "row",
		backgroundColor: "#2A2A2A",
		padding: 2,
		borderRadius: 4,
		gap: 2,
	},
	deviceButton: {
		padding: 6,
		borderRadius: 4,
	},
	activeDeviceButton: {
		backgroundColor: "#333",
	},
	dimensions: {
		color: "#fff",
		fontSize: 13,
	},
	separator: {
		color: "#666",
	},
	settingsButton: {
		padding: 6,
	},
	topBarRight: {
		flexDirection: "row",
		alignItems: "center",
		gap: 12,
	},
	iconButton: {
		padding: 6,
	},
	avatarGroup: {
		flexDirection: "row",
		marginLeft: 8,
	},
	avatar: {
		width: 28,
		height: 28,
		borderRadius: 14,
		justifyContent: "center",
		alignItems: "center",
		marginLeft: -8,
	},
	avatarText: {
		color: "#fff",
		fontSize: 11,
		fontWeight: "600",
	},
	inviteButton: {
		backgroundColor: "#2A2A2A",
		paddingHorizontal: 12,
		paddingVertical: 6,
		borderRadius: 4,
	},
	buttonText: {
		color: "#fff",
		fontSize: 13,
	},
	publishButton: {
		backgroundColor: "#82C91E",
		flexDirection: "row",
		alignItems: "center",
		paddingHorizontal: 12,
		paddingVertical: 6,
		borderRadius: 4,
		gap: 4,
	},
	publishText: {
		color: "#000",
		fontSize: 13,
		fontWeight: "600",
	},
	iconBar: {
		width: 40,
		backgroundColor: "#1A1A1A",
		borderRightWidth: 1,
		borderRightColor: "#333",
		paddingVertical: 8,
		alignItems: "center",
		gap: 8,
	},
	iconBarButton: {
		width: 32,
		height: 32,
		borderRadius: 6,
		justifyContent: "center",
		alignItems: "center",
	},
	leftSidebar: {
		maxWidth: 300,
		backgroundColor: "#1A1A1A",
		borderRightWidth: 1,
		borderRightColor: "#333",
	},
	quickAdd: {
		padding: 8,
		borderBottomWidth: 1,
		borderBottomColor: "#333",
	},
	sectionHeader: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 6,
	},
	sectionTitle: {
		color: "#fff",
		fontSize: 12,
		fontWeight: "500",
	},
	sectionHeaderIcons: {
		flexDirection: "row",
		gap: 8,
	},
	tabGroup: {
		flexDirection: "row",
		backgroundColor: "#2A2A2A",
		borderRadius: 4,
		padding: 2,
	},
	tab: {
		flex: 1,
		paddingVertical: 6,
		alignItems: "center",
		borderRadius: 2,
	},
	activeTab: {
		backgroundColor: "#333",
	},
	tabText: {
		color: "#fff",
		fontSize: 12,
	},
	componentsSection: {
		padding: 12,
		borderBottomWidth: 1,
		borderBottomColor: "#333",
	},
	componentGrid: {
		flexDirection: "row",
		flexWrap: "wrap",
		gap: 6,
		marginTop: 6,
	},
	componentBoxWrapper: {
		width: "100%",
		marginBottom: 8,
		borderWidth: 1,
		borderRadius: 4,
		backgroundColor: "transparent",
	},
	componentBox: {
		width: "100%",
		backgroundColor: "#222",
		borderRadius: 4,
		overflow: "hidden",
	},
	boxContent: {
		height: 60,
		padding: 12,
	},
	boxHeader: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		marginBottom: 4,
	},
	componentText: {
		color: "#fff",
		fontSize: 13,
		fontWeight: "500",
	},
	componentDescription: {
		color: "#666",
		fontSize: 11,
	},
	canvas: {
		flex: 1,
		backgroundColor: "#1A1A1A",
		padding: 20,
		display: "flex",
		flexDirection: "column",
	},
	canvasHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: 12,
	},
	canvasTitle: {
		color: "#fff",
		fontSize: 13,
	},
	canvasUrl: {
		color: "#666",
		fontSize: 13,
	},
	canvasContent: {
		flex: 1,
		backgroundColor: "#1A1A1A",
		borderRadius: 8,
		alignItems: "center",
		justifyContent: "center",
	},
	phoneContainer: {
		padding: 20,
		backgroundColor: "#2A2A2A",
		borderRadius: 60,
	},
	phoneFrame: {
		width: 375,
		height: 812,
		backgroundColor: "#fff",
		borderRadius: 40,
		overflow: "hidden",
		position: "relative",
	},
	phoneScreen: {
		flex: 1,
		backgroundColor: "#fff",
	},
	phoneContent: {
		flex: 1,
		paddingBottom: 80,
	},
	propertiesPanel: {
		width: 280,
		backgroundColor: "#1A1A1A",
		borderLeftWidth: 1,
		borderLeftColor: "#333",
	},
	propertyHeader: {
		padding: 16,
	},
	propertyTitleRow: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 16,
	},
	propertyTitle: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	breadcrumb: {
		flexDirection: "row",
		alignItems: "center",
		gap: 4,
	},
	breadcrumbText: {
		color: "#666",
		fontSize: 11,
	},
	propertyTabs: {
		flexDirection: "row",
		gap: 4,
		marginBottom: 16,
	},
	propertyTab: {
		paddingVertical: 6,
		paddingHorizontal: 12,
		borderRadius: 4,
	},
	activePropertyTab: {
		backgroundColor: "#2A2A2A",
	},
	propertyTabText: {
		color: "#fff",
		fontSize: 12,
	},
	alignmentTools: {
		flexDirection: "row",
		flexWrap: "wrap",
		gap: 4,
		marginBottom: 16,
	},
	alignButton: {
		width: 32,
		height: 32,
		backgroundColor: "#2A2A2A",
		borderRadius: 4,
		justifyContent: "center",
		alignItems: "center",
	},
	sizeControls: {
		marginBottom: 16,
	},
	controlLabel: {
		color: "#fff",
		fontSize: 13,
		marginBottom: 8,
	},
	sizeInputs: {
		flexDirection: "row",
		gap: 8,
		marginBottom: 8,
	},
	inputWithLabel: {
		flex: 1,
	},
	inputLabel: {
		color: "#666",
		fontSize: 11,
		marginBottom: 4,
	},
	inputContainer: {
		position: "relative",
	},
	numberInput: {
		backgroundColor: "#2A2A2A",
		color: "#fff",
		padding: 8,
		paddingRight: 24,
		borderRadius: 4,
		fontSize: 12,
	},
	inputUnit: {
		position: "absolute",
		right: 8,
		top: "50%",
		transform: [{ translateY: -6 }],
		color: "#666",
		fontSize: 11,
	},
	minMaxInputs: {
		flexDirection: "row",
		gap: 8,
		marginBottom: 8,
	},
	scaleCheckbox: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
	},
	checkboxLabel: {
		color: "#fff",
		fontSize: 12,
	},
	positionControls: {
		marginBottom: 16,
	},
	positionContainer: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		marginBottom: 8,
	},
	positionLabel: {
		width: 16,
		color: "#666",
		fontSize: 11,
		textAlign: "center",
	},
	positionInput: {
		flex: 1,
		backgroundColor: "#2A2A2A",
		color: "#fff",
		padding: 8,
		borderRadius: 4,
		fontSize: 12,
	},
	positionTypeContainer: {
		marginTop: 16,
	},
	dropdownContainer: {
		marginBottom: 12,
	},
	dropdownLabel: {
		color: "#666",
		fontSize: 11,
		marginBottom: 4,
	},
	dropdown: {
		backgroundColor: "#2A2A2A",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		padding: 8,
		borderRadius: 4,
	},
	dropdownText: {
		color: "#fff",
		fontSize: 12,
	},
	gridInputs: {
		marginTop: 16,
	},
	gridLabel: {
		color: "#666",
		fontSize: 11,
		marginBottom: 4,
	},
	gridInputGroup: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
	},
	gridInput: {
		width: 40,
		backgroundColor: "#2A2A2A",
		color: "#fff",
		padding: 8,
		borderRadius: 4,
		fontSize: 12,
		textAlign: "center",
	},
	gridInputLabel: {
		color: "#666",
		fontSize: 11,
	},
	menuBar: {
		flexDirection: "row",
		justifyContent: "space-evenly",
		alignItems: "center",
		backgroundColor: "#fff",
		borderTopWidth: 1,
		borderTopColor: "#eee",
		paddingVertical: 8,
		position: "absolute",
		bottom: 0,
		left: 0,
		right: 0,
	},
	menuItem: {
		minWidth: 64,
		maxWidth: 64,
		alignItems: "center",
		justifyContent: "center",
		padding: 8,
		position: "relative",
	},
	menuText: {
		fontSize: 12,
		color: "#666",
		marginTop: 4,
		textAlign: "center",
		width: "auto",
		whiteSpace: "nowrap",
		overflow: "visible",
	},
	activeMenuText: {
		color: "#82C91E",
	},
	caretLeft: {
		position: "absolute",
		left: 0,
		top: "50%",
		transform: [{ translateY: -7 }],
		zIndex: 2,
	},
	caretRight: {
		position: "absolute",
		right: 0,
		top: "50%",
		transform: [{ translateY: -7 }],
		zIndex: 2,
	},
	pageTitle: {
		fontSize: 24,
		fontWeight: "bold",
		marginBottom: 16,
		color: "#333",
	},
	placeholderText: {
		fontSize: 16,
		color: "#666",
	},
	addIcon: {
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: [{ translateX: -12 }, { translateY: -12 }],
	},
	componentSubtext: {
		color: "#666",
		fontSize: 10,
		marginTop: 2,
		textAlign: "center",
	},
	modalOverlay: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "rgba(0, 0, 0, 0.7)",
		justifyContent: "center",
		alignItems: "center",
		zIndex: 1000,
	},
	modal: {
		backgroundColor: "#2A2A2A",
		borderRadius: 8,
		padding: 24,
		width: "80%",
		maxWidth: 400,
	},
	modalHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: 16,
	},
	modalTitle: {
		color: "#fff",
		fontSize: 18,
		fontWeight: "600",
	},
	modalText: {
		color: "#ccc",
		fontSize: 14,
		marginBottom: 24,
		lineHeight: 20,
	},
	modalActions: {
		flexDirection: "row",
		justifyContent: "flex-end",
		gap: 12,
	},
	modalCancelButton: {
		paddingVertical: 8,
		paddingHorizontal: 16,
		borderRadius: 4,
		backgroundColor: "#333",
	},
	modalCancelText: {
		color: "#fff",
		fontSize: 14,
	},
	modalAddButton: {
		paddingVertical: 8,
		paddingHorizontal: 16,
		borderRadius: 4,
		backgroundColor: "#82C91E",
	},
	modalAddText: {
		color: "#000",
		fontSize: 14,
		fontWeight: "600",
	},
	pagesListContainer: {
		flex: 1,
		padding: 16,
	},
	pagesListHeader: {
		marginBottom: 16,
	},
	pagesListTitle: {
		fontSize: 18,
		fontWeight: "600",
		color: "#fff",
		marginBottom: 4,
	},
	pagesListSubtitle: {
		fontSize: 12,
		color: "#666",
	},
	pagesList: {
		flex: 1,
	},
	pageItem: {
		backgroundColor: "#1a1a1a",
		borderRadius: 8,
		marginBottom: 8,
		overflow: "hidden",
	},
	pageItemContent: {
		flexDirection: "row",
		alignItems: "center",
		padding: 12,
		backgroundColor: "#262626",
		position: "relative",
	},
	pageItemLeft: {
		flex: 1,
	},
	pageItemRight: {
		marginLeft: 8,
	},
	pageItemText: {
		color: "#fff",
		fontSize: 14,
	},
	activePageItem: {
		backgroundColor: "#333",
	},
	dragHandle: {
		cursor: "grab",
		color: "#666",
	},
	activeIconBarButton: {
		backgroundColor: "#2A2A2A",
	},
	layersContainer: {
		padding: 16,
	},
	layersHeader: {
		marginBottom: 16,
	},
	layersTitle: {
		color: "#fff",
		fontSize: 16,
		fontWeight: "600",
		marginBottom: 4,
	},
	layersSubtitle: {
		color: "#666",
		fontSize: 12,
	},
	layersList: {
		gap: 8,
	},
	layerSection: {
		backgroundColor: "#2A2A2A",
		borderRadius: 6,
		overflow: "hidden",
	},
	layerSectionHeader: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		padding: 12,
	},
	layerSectionLeft: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
	},
	layerSectionTitle: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	layerItems: {
		borderTopWidth: 1,
		borderTopColor: "#333",
	},
	layerItem: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		padding: 8,
		paddingLeft: 32,
		backgroundColor: "#262626",
		cursor: "grab",
	},
	layerItemDragging: {
		opacity: 0.5,
		backgroundColor: "#333",
	},
	layerItemLeft: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
	},
	layerItemRight: {
		flexDirection: "row",
		gap: 8,
	},
	layerItemTitle: {
		color: "#fff",
		fontSize: 12,
	},
	layersEmptyState: {
		padding: 16,
		alignItems: "center",
		justifyContent: "center",
		height: 200,
	},
	layersEmptyText: {
		color: "#666",
		fontSize: 14,
		textAlign: "center",
	},
	emptyStateContent: {
		flex: 1,
		justifyContent: "center",
		alignItems: "center",
	},
	statusBar: {
		height: 48,
		backgroundColor: "#fff",
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		paddingHorizontal: 22,
		paddingTop: 12,
	},
	statusBarLeft: {
		flex: 1,
		alignItems: "flex-start",
	},
	statusBarTime: {
		fontSize: 15,
		fontWeight: "600",
		color: "#000",
	},
	statusBarRight: {
		flex: 1,
		flexDirection: "row",
		justifyContent: "flex-end",
		alignItems: "center",
		gap: 6,
	},
	statusBarSignal: {
		flexDirection: "row",
		alignItems: "center",
	},
	statusBarBattery: {
		flexDirection: "row",
		alignItems: "center",
	},
	centerContent: {
		justifyContent: "center",
		alignItems: "center",
	},
	loadingText: {
		color: "#fff",
		fontSize: 16,
		marginBottom: 16,
	},
	errorText: {
		color: "#ff6b6b",
		fontSize: 16,
		marginBottom: 16,
		textAlign: "center",
	},
	retryButton: {
		backgroundColor: "#82C91E",
		paddingHorizontal: 16,
		paddingVertical: 8,
		borderRadius: 4,
	},
	retryButtonText: {
		color: "#000",
		fontSize: 14,
		fontWeight: "600",
	},
	revisionHistory: {
		position: "absolute",
		top: "100%",
		right: 200,
		width: 300,
		maxHeight: 400,
		backgroundColor: "#1A1A1A",
		borderRadius: 8,
		borderWidth: 1,
		borderColor: "#333",
		zIndex: 1000,
	},
	revisionHeader: {
		padding: 16,
		borderBottomWidth: 1,
		borderBottomColor: "#333",
	},
	revisionTitle: {
		fontSize: 16,
		fontWeight: "600",
		color: "#fff",
	},
	revisionSubtitle: {
		fontSize: 12,
		color: "#666",
		marginTop: 4,
	},
	revisionList: {
		maxHeight: 300,
	},
	revisionItem: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		padding: 12,
		borderBottomWidth: 1,
		borderBottomColor: "#333",
	},
	revisionInfo: {
		flex: 1,
	},
	revisionType: {
		fontSize: 14,
		color: "#fff",
	},
	revisionTime: {
		fontSize: 12,
		color: "#666",
		marginTop: 2,
	},
	iconButtonDisabled: {
		opacity: 0.5,
	},
	iconButtonActive: {
		backgroundColor: "#2B2B2B",
	},
	revisionHistoryEmpty: {
		padding: 16,
		alignItems: "center",
	},
	emptyText: {
		color: "#666",
		fontSize: 14,
	},
	deleteButton: {
		padding: 8,
	},
	deleteConfirm: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "rgba(42, 42, 42, 0.95)",
		padding: 12,
		borderRadius: 8,
		zIndex: 1000,
		justifyContent: "center",
		alignItems: "center",
	},
	deleteConfirmText: {
		color: "#fff",
		fontSize: 14,
		marginBottom: 12,
		textAlign: "center",
	},
	deleteConfirmButtons: {
		flexDirection: "row",
		justifyContent: "center",
		gap: 8,
	},
	deleteConfirmCancel: {
		paddingVertical: 6,
		paddingHorizontal: 12,
		borderRadius: 4,
		backgroundColor: "#333",
	},
	deleteConfirmCancelText: {
		color: "#fff",
		fontSize: 12,
	},
	deleteConfirmDelete: {
		paddingVertical: 6,
		paddingHorizontal: 12,
		borderRadius: 4,
		backgroundColor: "#ff6b6b",
	},
	deleteConfirmDeleteText: {
		color: "#fff",
		fontSize: 12,
		fontWeight: "500",
	},
	emptyStateText: {
		color: "#666",
		fontSize: 13,
		textAlign: "center",
		padding: 12,
		width: "100%",
	},
	pageArrowsContainer: {
		position: "absolute",
		left: "50%",
		top: "50%",
		transform: [{ translateX: -12 }, { translateY: -24 }],
		flexDirection: "column",
		alignItems: "center",
		gap: 2,
	},
	pageArrow: {
		width: 24,
		height: 24,
		justifyContent: "center",
		alignItems: "center",
		borderRadius: 4,
	},
	badge: {
		paddingHorizontal: 8,
		paddingVertical: 4,
		borderRadius: 12,
		minWidth: 32,
		justifyContent: "center",
		alignItems: "center",
		marginLeft: 4,
	},
	badgeText: {
		color: "#fff",
		fontSize: 10,
		fontWeight: "600",
	},
	badgeContainer: {
		position: "absolute",
		top: -4,
		right: -2,
		flexDirection: "row",
		zIndex: 1,
	},
	unsavedChangesGem: {
		flexDirection: "row",
		alignItems: "center",
		borderWidth: 1,
		borderColor: "goldenrod",
		borderRadius: 4,
		paddingHorizontal: 8,
		paddingVertical: 4,
		gap: 6,
		marginLeft: 8,
	},
	unsavedChangesText: {
		color: "goldenrod",
		fontSize: 12,
	},
	divider: {
		width: 1,
		height: 24,
		backgroundColor: "#333",
		marginHorizontal: 12,
	},
	designButton: {
		flexDirection: "row",
		alignItems: "center",
		paddingHorizontal: 12,
		paddingVertical: 6,
		borderRadius: 4,
		backgroundColor: "#2A2A2A",
		marginLeft: 8,
	},
	designButtonText: {
		color: "#fff",
		fontSize: 13,
		marginLeft: 6,
	},
});

export default Editor;
